import _, { isEmpty, isNil } from 'lodash';
import { DateTime } from 'luxon';
import IIdable from 'types/Idable';
import IMemberTimeDocument from 'types/MemberTimeDocument';
import ITimeEntry from 'types/TimeEntry';
import ITimeOff from 'types/TimeOff';
import ITimeRange from 'types/TimeRange';
import { stringUtils } from 'utils';
import ITimeEntryReportWithHeaderData from '../../TimeEntryDataReport/types/TimeEntryReportWithHeaderData';
import { ITimeEntryDataTableRow } from '../TimeEntryDataTable';
import useTimeEntryToTimeEntryRowMap from './useTimeEntryToTimeEntryRowMap';
import { useTimeOffToTimeOffRowMap } from './useTimeOffToTimeOffRowMap';

export interface ITimeEntryDataTableData<T extends IIdable<string>> {
  item: T;
  entries: ITimeEntry[];
  timeOffs: ITimeOff[];
  cursor?: string;
}

export interface ITimeEntryReportRowActions {
  logs: boolean;
  locations: boolean;
  conflicts: boolean;
  splitEntry: boolean;
}

export function useTimeEntryDataTableRow() {
  const convertTimeEntryToRow = useTimeEntryToTimeEntryRowMap();
  const convertTimeOffToRow = useTimeOffToTimeOffRowMap();

  return function getRows<T extends IIdable<string>>(
    timeRange: ITimeRange<DateTime>,
    items: Array<ITimeEntryDataTableData<T>>,
    getName: (item: T) => string,
    getSignature?: (item: T) => IMemberTimeDocument | null | undefined
  ) {
    return items.map((item): ITimeEntryReportWithHeaderData => {
      let rows: ITimeEntryDataTableRow[] = item.entries.flatMap((e) => convertTimeEntryToRow(e, timeRange));
      if (!isEmpty(item.timeOffs)) {
        rows = rows.concat(item.timeOffs.map(convertTimeOffToRow));
      }
      const total = stringUtils.getTotalAsHoursMinutesSeconds(_.sumBy(rows, (r) => r.totalSeconds));
      const title = getName(item.item);
      const hasSignature = getSignature && !isNil(getSignature(item.item)) ? !getSignature(item.item)?.canceled : false;

      return {
        id: item.item.id,
        title,
        total,
        rows,
        hasSignature,
      };
    });
  };
}
